import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Building2, Settings, LogOut,
  Menu, X, Shield, ChevronRight, Briefcase
} from 'lucide-react';

const NAV = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/firmalar', icon: Building2, label: 'Firmalar' },
  { to: '/kurumlar', icon: Briefcase, label: 'Kurumlar' },
  { to: '/audit-log', icon: Shield, label: 'Audit Log' },
  { to: '/ayarlar', icon: Settings, label: 'Ayarlar' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F4F6F9]">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-navy-900/50 z-20 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative inset-y-0 left-0 z-30
          flex flex-col
          bg-gradient-to-b from-navy-700 to-navy-900
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-60'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo alanı */}
        <div className={`flex items-center gap-3 px-4 py-5 border-b border-white/10 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-lg bg-gold-400 flex items-center justify-center flex-shrink-0">
            <span className="text-navy-900 font-bold text-sm font-display">K</span>
          </div>
          {!collapsed && (
            <div className="animate-fade-in overflow-hidden">
              <p className="text-white font-semibold text-sm leading-tight">Firma Paneli</p>
              <p className="text-navy-300 text-xs">Kanguroo</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                isActive ? 'nav-item-active' : 'nav-item'
              }
              title={collapsed ? label : undefined}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
              {!collapsed && (
                <ChevronRight size={14} className="ml-auto opacity-30" />
              )}
            </NavLink>
          ))}
        </nav>

        {/* Alt alan — kullanıcı */}
        <div className="px-2 py-3 border-t border-white/10">
          <div className={`flex items-center gap-3 px-2 py-2 rounded-xl ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-7 h-7 rounded-lg bg-gold-400/20 border border-gold-400/30 flex items-center justify-center flex-shrink-0">
              <span className="text-gold-300 text-xs font-semibold">
                {user?.email?.[0]?.toUpperCase()}
              </span>
            </div>
            {!collapsed && (
              <div className="flex-1 overflow-hidden animate-fade-in">
                <p className="text-white text-xs font-medium truncate">{user?.email}</p>
                <p className="text-navy-400 text-xs">Admin</p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className={`w-full mt-1 flex items-center gap-2 px-2 py-2 rounded-xl text-navy-300 hover:text-red-400 hover:bg-red-500/10 transition-all text-sm ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Çıkış' : undefined}
          >
            <LogOut size={16} />
            {!collapsed && <span>Çıkış Yap</span>}
          </button>
        </div>

        {/* Collapse toggle — sadece desktop */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-navy-200 shadow-card items-center justify-center hover:bg-navy-50 transition-colors"
        >
          <ChevronRight size={12} className={`text-navy-500 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </button>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-navy-100 flex items-center px-4 gap-4 flex-shrink-0 shadow-sm">
          <button
            className="lg:hidden btn-ghost p-1.5"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex-1" />
          <span className="text-xs text-navy-400 font-mono hidden sm:block">
            firma.kanguroo.com.tr
          </span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
